import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../App';
import { ASSETS } from '../constants/site';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const paypalToken = params.get('token');
  const [state, setState] = useState({ phase: 'checking', info: null });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const pollStripe = async (attempts = 0) => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setState({ phase: 'paid', info: data });
          return;
        }
      } catch { /* retry */ }
      if (attempts < 8) setTimeout(() => pollStripe(attempts + 1), 2000);
      else setState({ phase: 'pending', info: null });
    };

    const capturePaypal = async () => {
      try {
        const { data } = await api.post('/paypal/capture', { order_id: paypalToken });
        setState(data.payment_status === 'paid' ? { phase: 'paid', info: data } : { phase: 'pending', info: data });
      } catch {
        setState({ phase: 'pending', info: null });
      }
    };

    if (sessionId) pollStripe();
    else if (paypalToken) capturePaypal();
    else setState({ phase: 'pending', info: null });
  }, [sessionId, paypalToken]);

  return (
    <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="gold-ring rounded-3xl bg-[#0F1C33]/70 p-10 max-w-md w-full text-center"
        data-testid="payment-success-page"
      >
        <img src={ASSETS.icon} alt="GSLAB" className="h-16 w-16 rounded-xl mx-auto mb-6" />
        {state.phase === 'checking' && (
          <>
            <Loader2 size={36} className="text-amber-400 animate-spin mx-auto mb-5" />
            <h1 className="font-display font-bold text-2xl mb-2">Confirming payment…</h1>
            <p className="text-sm text-slate-400">Securing your order with our payment processor.</p>
          </>
        )}
        {state.phase === 'paid' && (
          <>
            <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-5" />
            <h1 className="font-display font-bold text-2xl mb-2">Payment confirmed.</h1>
            <p className="text-sm text-slate-400 mb-2">{state.info?.package_name || 'Your GSLAB order'} is locked in.</p>
            <p className="text-sm text-slate-400 mb-8">Gabe will reach out within one business day to schedule your Business Mapping session.</p>
            <div className="flex gap-3">
              <a href="/account" data-testid="payment-success-account-link" className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-5 py-3 text-sm">
                My Account
              </a>
              <a href="/" className="flex-1 border border-slate-600 text-slate-200 rounded-full px-5 py-3 text-sm hover:border-amber-500/50 transition-colors">
                Back Home
              </a>
            </div>
          </>
        )}
        {state.phase === 'pending' && (
          <>
            <AlertCircle size={40} className="text-amber-400 mx-auto mb-5" />
            <h1 className="font-display font-bold text-2xl mb-2">Almost there.</h1>
            <p className="text-sm text-slate-400 mb-8">
              Your payment is being processed. If you completed checkout, it will confirm shortly — check your account in a moment.
            </p>
            <a href="/account" className="inline-block border border-slate-600 text-slate-200 rounded-full px-7 py-3 text-sm hover:border-amber-500/50 transition-colors">
              Check My Account
            </a>
          </>
        )}
      </motion.div>
    </div>
  );
}
